import { useEffect, useState, useMemo } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Presentation, Upload, Download, Trash2, FileText, FileType2 } from 'lucide-react';

/*
 * v1.1.7 — Materiali Didattici Docenti
 * Versione semplificata: solo upload PDF/PPT/PPTX + lista + download + delete.
 * Nessun template Jinja, nessuna compilazione automatica.
 */

const LIVELLO_COLORS = {
  generale: 'bg-slate-100 text-slate-800',
  basso: 'bg-emerald-100 text-emerald-800',
  medio: 'bg-amber-100 text-amber-800',
  alto: 'bg-red-100 text-red-800',
  dirigenti: 'bg-indigo-100 text-indigo-800',
  preposti: 'bg-cyan-100 text-cyan-800',
  rls: 'bg-fuchsia-100 text-fuchsia-800',
  rspp: 'bg-purple-100 text-purple-800',
  haccp: 'bg-lime-100 text-lime-800',
  altro: 'bg-slate-100 text-slate-800',
};

const ADMIN_ROLES = ['super_admin', 'admin', 'amministratore'];
const ALLOWED_EXT = ['.pdf', '.ppt', '.pptx', '.pptm', '.ppsx', '.pps'];

export default function DocentePptPage() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);

  const [items, setItems] = useState([]);
  const [livelli, setLivelli] = useState([]);
  const [filterLivello, setFilterLivello] = useState('all');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [uploadForm, setUploadForm] = useState({
    file: null, nome: '', descrizione: '', livello_rischio: 'generale', ore_consigliate: '',
  });

  const load = async () => {
    try {
      const [tplRes, livRes] = await Promise.all([
        api.get('/docente-ppt/templates'),
        api.get('/docente-ppt/livelli'),
      ]);
      setItems(tplRes.data || []);
      setLivelli(livRes.data?.livelli || []);
    } catch (e) {
      toast.error(`Errore caricamento: ${e.response?.data?.detail || e.message}`);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filterLivello === 'all') return items;
    return items.filter(t => t.livello_rischio === filterLivello);
  }, [items, filterLivello]);

  const handleUpload = async () => {
    if (!uploadForm.file) { toast.error('Seleziona un file PDF, PPT o PPTX'); return; }
    const ext = '.' + uploadForm.file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error(`Formato non valido. Accettati: ${ALLOWED_EXT.join(', ')}`);
      return;
    }
    if (!uploadForm.nome.trim()) {
      // auto-fill nome from filename
      uploadForm.nome = uploadForm.file.name.replace(/\.[^.]+$/, '');
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadForm.file);
      fd.append('nome', uploadForm.nome.trim());
      fd.append('descrizione', uploadForm.descrizione || '');
      fd.append('livello_rischio', uploadForm.livello_rischio);
      if (uploadForm.ore_consigliate) fd.append('ore_consigliate', uploadForm.ore_consigliate);
      await api.post('/docente-ppt/templates', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      toast.success('File caricato con successo');
      setUploadOpen(false);
      setUploadForm({ file: null, nome: '', descrizione: '', livello_rischio: 'generale', ore_consigliate: '' });
      load();
    } catch (e) {
      toast.error(`Errore upload: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (tid) => {
    if (!window.confirm('Eliminare definitivamente questo materiale?')) return;
    try {
      await api.delete(`/docente-ppt/templates/${tid}`);
      toast.success('Eliminato');
      load();
    } catch (e) {
      toast.error('Errore eliminazione');
    }
  };

  const handleDownload = async (tid, filename) => {
    try {
      const r = await api.get(`/docente-ppt/templates/${tid}/download`, { responseType: 'blob' });
      const blob = new Blob([r.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename || 'materiale.pdf'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Errore download');
    }
  };

  const formatSize = (b) => b ? `${(b / 1024).toFixed(0)} KB` : '';

  const isPdf = (fname) => (fname || '').toLowerCase().endsWith('.pdf');

  return (
    <div className="space-y-6" data-testid="docente-ppt-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Presentation className="w-8 h-8 text-purple-600"/>
            Materiali Didattici Docenti
          </h1>
          <p className="text-muted-foreground">
            Carica e scarica slide e dispense in formato <strong>PDF, PPT o PPTX</strong> usate dai docenti durante le sessioni formative.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setUploadOpen(true)} data-testid="upload-ppt-btn">
            <Upload className="w-4 h-4 mr-2"/>Carica materiale
          </Button>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-sm">Filtra per livello rischio:</Label>
        <Select value={filterLivello} onValueChange={setFilterLivello}>
          <SelectTrigger className="w-64" data-testid="filter-livello">
            <SelectValue/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            {livelli.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Badge variant="secondary">{filtered.length} materiale/i</Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            <Presentation className="w-12 h-12 mx-auto mb-3 opacity-30"/>
            <p>Nessun materiale disponibile{filterLivello !== 'all' ? ' per questo livello' : ''}.</p>
            {isAdmin && <p className="text-xs mt-1">Clicca &quot;Carica materiale&quot; per iniziare.</p>}
          </div>
        )}
        {filtered.map(t => {
          const pdf = isPdf(t.original_filename);
          return (
            <Card key={t.id} className="hover:shadow-lg transition" data-testid={`ppt-card-${t.id}`}>
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base truncate flex items-center gap-2">
                      {pdf ? <FileText className="w-4 h-4 text-red-600 shrink-0"/> : <FileType2 className="w-4 h-4 text-orange-600 shrink-0"/>}
                      {t.nome}
                    </CardTitle>
                    <CardDescription className="text-xs mt-1 truncate">
                      {t.original_filename} · {formatSize(t.file_size)}
                      {t.n_slides ? ` · ${t.n_slides} slide` : ''}
                    </CardDescription>
                  </div>
                  <Badge className={LIVELLO_COLORS[t.livello_rischio] || 'bg-slate-100'}>
                    {t.livello_rischio}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {t.descrizione && <p className="text-sm text-slate-600 line-clamp-2">{t.descrizione}</p>}
                {t.ore_consigliate && (
                  <p className="text-xs text-slate-500">Ore consigliate: <strong>{t.ore_consigliate}h</strong></p>
                )}
                <p className="text-[11px] text-slate-400">Caricato da {t.uploaded_by}</p>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={() => handleDownload(t.id, t.original_filename)} data-testid={`dl-${t.id}`}>
                    <Download className="w-3 h-3 mr-1"/>Scarica
                  </Button>
                  {isAdmin && (
                    <Button size="sm" variant="ghost" className="text-red-600 ml-auto" onClick={() => handleDelete(t.id)} data-testid={`del-${t.id}`}>
                      <Trash2 className="w-3 h-3"/>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* UPLOAD DIALOG */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Carica materiale didattico</DialogTitle>
            <DialogDescription>
              Carica una slide o dispensa in formato PDF, PPT o PPTX. Il file sarà disponibile a tutti i docenti per il download.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>File PDF / PPT / PPTX *</Label>
              <Input
                type="file"
                accept=".pdf,.ppt,.pptx,.pptm,.ppsx,.pps"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setUploadForm(form => ({
                    ...form,
                    file: f,
                    nome: form.nome || (f ? f.name.replace(/\.[^.]+$/, '') : ''),
                  }));
                }}
                data-testid="upload-file"
              />
              {uploadForm.file && (
                <p className="text-xs text-slate-500 mt-1">
                  Selezionato: <strong>{uploadForm.file.name}</strong> ({Math.round(uploadForm.file.size/1024)} KB)
                </p>
              )}
            </div>
            <div>
              <Label>Nome (auto-compilato dal file)</Label>
              <Input
                value={uploadForm.nome}
                onChange={(e) => setUploadForm(f => ({ ...f, nome: e.target.value }))}
                placeholder="Es. Sicurezza generale lavoratori"
                data-testid="upload-nome"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Livello rischio</Label>
                <Select value={uploadForm.livello_rischio} onValueChange={(v) => setUploadForm(f => ({ ...f, livello_rischio: v }))}>
                  <SelectTrigger data-testid="upload-livello"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {livelli.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ore consigliate</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={uploadForm.ore_consigliate}
                  onChange={(e) => setUploadForm(f => ({ ...f, ore_consigliate: e.target.value }))}
                  placeholder="es. 4"
                />
              </div>
            </div>
            <div>
              <Label>Descrizione (opzionale)</Label>
              <Textarea
                value={uploadForm.descrizione}
                onChange={(e) => setUploadForm(f => ({ ...f, descrizione: e.target.value }))}
                rows={2}
                placeholder="Es. Materiale aggiornato Accordo Stato-Regioni 2011..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={loading}>Annulla</Button>
            <Button onClick={handleUpload} disabled={loading} data-testid="upload-submit">
              {loading ? 'Caricamento...' : 'Carica'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
